import React from 'react';

export type StatTone = 'risk' | 'warn' | 'ok' | 'neutral';

const TONE_COLOR: Record<StatTone, string> = {
  risk: 'var(--color-danger)',
  warn: 'var(--color-warning)',
  ok: 'var(--color-success)',
  neutral: 'var(--color-gray-400)'
};

export interface StatCell {
  key: string;
  label: string;
  value: React.ReactNode;
  note?: React.ReactNode;
  tone?: StatTone;
  onClick?: () => void;
  isSelected?: boolean;
}

/**
 * Washi stat row: one hairline-ruled strip instead of a box per number.
 * Canonical stat idiom for the platform — prefer this over boxed stat
 * cards / .stat-box in new code. Lifted from carrier/tabs/Overview.tsx,
 * the design exemplar.
 */
export const StatStrip: React.FC<{ cells: StatCell[] }> = ({ cells }) => (
  <div
    className="grid grid-cols-2 lg:grid-cols-[repeat(var(--cols),minmax(0,1fr))] border-y border-gray-200"
    style={{ '--cols': cells.length } as React.CSSProperties}
  >
    {cells.map((cell, i) => {
      const body = (
        <>
          <div className="flex items-center gap-1.5 text-xs text-gray-500 tracking-[0.04em]">
            {cell.tone && cell.tone !== 'neutral' && (
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: TONE_COLOR[cell.tone] }} aria-hidden="true" />
            )}
            {cell.label}
          </div>
          <div className="mt-1 font-serif text-[28px] leading-tight text-gray-900 tabular-nums">{cell.value}</div>
          {cell.note && <div className="mt-0.5 text-xs text-gray-400">{cell.note}</div>}
        </>
      );
      // Divider before every cell except the first; on the 2-column mobile grid only before odd cells
      const divider = i === 0 ? '' : i % 2 === 1 ? 'pl-5 border-l' : 'lg:pl-5 lg:border-l';
      const cellClass = `text-left py-4 pr-4 ${divider} border-gray-200`;
      return cell.onClick ? (
        <button
          key={cell.key}
          type="button"
          onClick={cell.onClick}
          aria-pressed={cell.isSelected}
          className={`${cellClass} cursor-pointer transition-colors hover:bg-gray-100/60 ${cell.isSelected ? 'shadow-[inset_0_-2px_0_var(--color-gray-900)]' : ''}`}
        >
          {body}
        </button>
      ) : (
        <div key={cell.key} className={cellClass}>{body}</div>
      );
    })}
  </div>
);

/** Section heading paired with StatStrip/card-modern sections. */
export const SectionHeading: React.FC<{ title: string; aside?: string }> = ({ title, aside }) => (
  <div className="flex items-baseline justify-between gap-4 mb-3">
    <h3 className="font-serif text-base text-gray-900">{title}</h3>
    {aside && <span className="text-xs text-gray-400">{aside}</span>}
  </div>
);

export default StatStrip;
